import React from "react";
import Input from "../Components/shared/Input";
import topTost from "./topTost";
import { fetchData, fetchFormData } from "./options";

const DocumentModal = ({
  show,
  onClose,
  onConfirm,
  selectedInquiryId,
  documentsImage,
  documents,
  setDocuments,
}) => {
  const today = new Date().toISOString().split("T")[0];

  if (!show) return null;
  const userId = localStorage.getItem("id");

  const removeDocument = (index) => {
    const updated = [...documents];
    updated.splice(index, 1);
    setDocuments(updated);
  };
  const handleNameChange = (index, value) => {
    const updated = [...documents];
    updated[index].name = value;
    setDocuments(updated);
  };

  const handleImageChange = (index, files) => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    ];

    const validFiles = files.filter((file) => allowedTypes.includes(file.type));

    if (validFiles.length !== files.length) {
      topTost({
        title: "Only JPG, PNG, PDF, DOC, and DOCX files are allowed.",
        icon: "error",
      });
    }

    const updated = [...documents];
    updated[index].images = validFiles;
    setDocuments(updated);
  };

  const addDocument = () => {
    setDocuments([...documents, { id: "", name: "", images: [] }]);
  };

  const handleDocumentSubmit = async () => {
    // Validate all documents
    for (let doc of documents) {
      if (!doc.name.trim()) {
        topTost({
          title: "Please enter a document name for all fields.",
          icon: "error",
        });
        return;
      }
    }

    try {
      // Convert all documents to base64
      const convertedDocuments = await Promise.all(
        documents.map(async (doc) => {
          const base64Files = await Promise.all(
            doc.images.map(
              (file) =>
                new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result);
                  reader.onerror = (err) => reject(err);
                  reader.readAsDataURL(file);
                })
            )
          );

          return base64Files.map((base64File) => ({
            file: base64File,
            document_type: doc.name,
          }));
        })
      );

      // Flatten the array (because map returns arrays of arrays)
      const allDocuments = convertedDocuments.flat();

      const payload = {
        inquiry_id: selectedInquiryId,
        documents: allDocuments,
      };

      const response = await fetchData(
        "lead-documents",
        payload,
        "POST",
        false
      );

      if (response?.code === 200 || response) {
        topTost({
          title: response.message || "Documents uploaded successfully",
          icon: "success",
        });

        // Update document IDs (mark them as saved)
        // Assuming response.data.documents is an array of uploaded docs with ID
        const updatedDocs = documents.map((doc, index) => ({
          ...doc,
          id: response?.data?.documents?.[index]?.id ?? doc.id,
        }));

        setDocuments(updatedDocs);

        onClose?.(); // Close modal
      } else if (response && response.code === 422) {
        topTost({
          title: response.message || "The documents field is required.",
          icon: "error",
        });
      } else {
        topTost({
          title: response.message,
          icon: "success",
        });
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
      topTost({
        title: "The documents field is required.",
        icon: "error",
      });
    }
  };

  const handleRemoveDocument = async (docId, index) => {
    if (!docId) return;

    try {
      const form = new FormData();
      form.append("id", docId); // ✅ Using real backend ID

      const response = await fetchFormData("delete-document", form, "POST");

      if (response && response.code === 200) {
        topTost({
          title: response.message || "Document deleted successfully.",
          icon: "success",
        });

        const updatedDocs = [...documents];
        updatedDocs.splice(index, 1);
        setDocuments(updatedDocs);
      } else {
        topTost({
          title: response?.message || "Failed to delete the document.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Delete document error:", error);
      topTost({
        title: "An unexpected error occurred.",
        icon: "error",
      });
    }
  };

  const handleCancel = () => {
    onClose?.(); // Close the modal
    window.location.reload(); // Reload the page
  };

  return (
    <div
      className="modal d-block"
      tabIndex={-1}
      role="dialog"
      style={{ backdropFilter: "blur(4px)" }}
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Update Document Image</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleCancel}
            ></button>
          </div>
          <div className="modal-body">
            {documents.map((doc, index) => (
              <div key={index} className="document-section">
                <div className="row align-items-end mb-3 document-row">
                  {/* Document Name */}
                  <div className="col-12 col-md-4 mb-2 mb-md-0">
                    <label>
                      Document Name<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={doc.name}
                      onChange={(e) => handleNameChange(index, e.target.value)}
                      placeholder="Enter Document Name"
                    />
                  </div>

                  {/* Upload Images */}
                  <div className="col-12 col-md-5 mb-2 mb-md-0">
                    <label>
                      Upload Images<span className="text-danger">*</span>
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      multiple
                      onChange={(e) =>
                        handleImageChange(index, Array.from(e.target.files))
                      }
                    />
                  </div>

                  {/* Buttons */}
                  <div className="col-12 col-md-2 d-flex gap-2 mb-2">
                    {doc.id ? (
                      <>
                        {index === documents.length - 1 && (
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={addDocument}
                          >
                            +
                          </button>
                        )}
                        <button
                          title="Delete"
                          type="button"
                          className="btn btn-white btn-sm"
                          onClick={() => handleRemoveDocument(doc.id, index)}
                        >
                          ❌
                        </button>
                      </>
                    ) : (
                      documents.length > 1 && (
                        <button
                          disabled={!!doc.id}
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeDocument(index)}
                        >
                          -
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* Divider between rows */}
                {index < documents.length - 1 && <hr />}
              </div>
            ))}
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleDocumentSubmit}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentModal;
